import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Center,
  Flex,
  Group,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text
} from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useFeatureFlag } from '@/store/slices/api/featureFlagsApiSlice';
import { useGetAllPatientsQuery } from '@/store/slices/api/patient/patientListApiSlice';
import {
  clearCurrentReportFilters,
  clearPreviousReportFilters,
  setCurrentPage,
  setCurrentReportFilters,
  setPageLimit,
  setPreviousReportFilters,
  setSearchValue,
  setSort
} from '@/store/slices/patientList/patientListFiltersSlice';
import FiltersModal from '@/components/FiltersModal';

import { PatientData } from '@/types/patientList';

import DebounceSearchInput from '../PatientsPage/components/CustomDebounceSearch';

import { AsyncBatchDownloadModal } from './AsyncBatchDownloadModal';
import CombinedFiltersDropdown from './CombinedFiltersDropdown';
import { useNotDownloadedReports } from './hooks/useNotDownloadedReports';
import styles from './PatientListPage.module.scss';
import { PatientTableRow } from './PatientTableRow';
import TableHeader from './TableHeader';
import { TablePagination } from './TablePagination';
import TableSkeleton from './TableSkeleton';

type PreviousReportFilters = {
  colors?: string[];
  isRecentSigned?: true;
  isDownloaded?: true;
  recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE';
};

type CurrentReportFilters = {
  colors?: string[];
  isNonPractitionerSigned?: true;
  isPendingReview?: true;
};

type SortDirection = 'ASC' | 'DESC' | null;

const PageHeader: React.FC<{
  patientCount?: number;
  isLoading: boolean;
  searchValue: string | undefined;
  onSearchChange: (value: string | undefined) => void;
  notDownloadedCount?: number;
  isNotDownloadedLoading: boolean;
  onNotDownloadedClick: () => void;
  currentReportFilters?: CurrentReportFilters;
  isNewFiltersUXEnabled: boolean;
  previousReportFilters?: PreviousReportFilters;
  onCurrentReportFilterChange: (
    filters: string[],
    isNonPractitioner: boolean,
    isPendingReview: boolean
  ) => void;
  onPreviousReportFilterChange: (
    filters: string[],
    isRecentSigned?: boolean,
    isDownloaded?: boolean,
    recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE'
  ) => void;
  selectedReportsCount?: number;
}> = ({
  patientCount,
  isNewFiltersUXEnabled,
  isLoading,
  searchValue,
  onSearchChange,
  notDownloadedCount,
  isNotDownloadedLoading,
  onNotDownloadedClick,
  currentReportFilters,
  previousReportFilters,
  onCurrentReportFilterChange,
  onPreviousReportFilterChange,
  selectedReportsCount
}) => (
  <div className={styles.headerSection}>
    <div className={styles.titleSection}>
      <Text className={styles.title}>
        All Patients&nbsp;
        {!isLoading && patientCount !== undefined && (
          <Text component="span" className={styles.subTitle}>
            {patientCount.toLocaleString()} PATIENTS
          </Text>
        )}
      </Text>

      <DebounceSearchInput
        placeholder="🔍︎    Search patients"
        searchValue={searchValue}
        setSearchValue={onSearchChange}
        style={{ width: 500, marginLeft: 16 }}
        size="sm"
      />
    </div>

    <Flex>
      {isNewFiltersUXEnabled ? (
        <CombinedFiltersDropdown
          selectedCurrentReportFilters={currentReportFilters?.colors || []}
          selectedPreviousReportFilters={previousReportFilters?.colors || []}
          isNonPractitionerSignedCRSelected={currentReportFilters?.isNonPractitionerSigned || false}
          isPendingReviewSelectedCRSelected={currentReportFilters?.isPendingReview || false}
          isRecentSignedSelected={previousReportFilters?.isRecentSigned || false}
          isDownloadedSelected={previousReportFilters?.isDownloaded || false}
          recentSignedSortBy={previousReportFilters?.recentSignedSortBy || 'SIGNED_DATE'}
          currentReportType={
            currentReportFilters && Object.keys(currentReportFilters).length > 0
              ? 'current'
              : 'previous'
          }
          onApplyReportFilters={(
            filters,
            isNonPractitioner,
            isPendingReview,
            isRecentSigned,
            isNotDownloaded,
            reportType,
            recentSignedSortBy
          ) => {
            if (reportType === 'previous') {
              onPreviousReportFilterChange(
                filters,
                isRecentSigned,
                isNotDownloaded,
                recentSignedSortBy
              );
            } else {
              onCurrentReportFilterChange(filters, isNonPractitioner, isPendingReview);
            }
          }}
          showNoReportOption={true}
          showPreviousReportOptions={true}
        />
      ) : (
        <FiltersModal />
      )}

      <Button
        loading={isNotDownloadedLoading}
        onClick={onNotDownloadedClick}
        disabled={selectedReportsCount ? false : !notDownloadedCount || notDownloadedCount === 0}
        ml="md">
        <IconDownload size={16} /> &nbsp;&nbsp;
        {selectedReportsCount && selectedReportsCount > 0 ? 'Previous Reports' : 'Not Downloaded'}
        {!isNotDownloadedLoading && (
          <Text component="span">({selectedReportsCount || notDownloadedCount || 0})</Text>
        )}
      </Button>
    </Flex>
  </div>
);

const PatientListPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const {
    searchValue,
    previousReportFilters,
    currentReportFilters,
    sortBy,
    sortDirection,
    currentPage,
    pageLimit: rawPageLimit
  } = useAppSelector((state) => state.patientListFilters);

  const isNewFiltersUXEnabled = useFeatureFlag('new_filters_dx');

  // Default pageLimit to 10 if not present
  const pageLimit = rawPageLimit || 10;
  const skip = (currentPage - 1) * pageLimit;
  const [selectedPatientIds, setSelectedPatientIds] = useState<Set<string>>(new Set());
  const [selectedReports, setSelectedReports] = useState<string[]>([]); // accumulates notDownloadedReports

  // Helper functions to check if filters exist
  const hasPreviousFilters = previousReportFilters && Object.keys(previousReportFilters).length > 0;
  const hasCurrentFilters = currentReportFilters && Object.keys(currentReportFilters).length > 0;

  // Only send one type of filter at a time (API requirement)
  const apiPreviousReportFilters =
    hasPreviousFilters && !hasCurrentFilters ? previousReportFilters : undefined;
  const apiCurrentReportFilters =
    hasCurrentFilters && !hasPreviousFilters ? currentReportFilters : undefined;

  const { filters: globalFilter } = useAppSelector((state) => state.preferences);
  const {
    data,
    isLoading,
    error,
    isFetching,
    refetch: patientListRefetch
  } = useGetAllPatientsQuery({
    skip,
    pageLimit: pageLimit,
    forV2: true,
    isNew: false,
    status: 'All',
    search: searchValue && searchValue.length >= 3 ? searchValue : undefined,
    clinicIds: globalFilter?.clinics?.map((clinic) => clinic.value) ?? [],
    practitionersIds: globalFilter?.practitioners?.map((practitioner) => practitioner.value) ?? [],
    vendors: globalFilter?.vendors?.map((vendor) => vendor.value) ?? [],
    deviceTypes: globalFilter?.deviceTypes?.map((deviceType) => deviceType.value) ?? [],
    patientStatuses: globalFilter?.patientStatuses?.map((status) => status.value) ?? [],
    currentReportFilters: apiCurrentReportFilters,
    previousReportFilters: apiPreviousReportFilters,
    sortBy: sortBy || undefined,
    sortDirection: sortDirection || undefined
  });

  const resetSelection = () => {
    setSelectedPatientIds(new Set());
    setSelectedReports([]);
  };

  const {
    notDownloadedCount,
    isNotDownloadedLoading,
    batchState,
    setBatchState,
    onDownloadAllNotDownloadedReports,
    handleBatchDownload,
    handleClose
  } = useNotDownloadedReports(
    selectedReports.length > 0 ? selectedReports.map(Number) : undefined,
    resetSelection,
    patientListRefetch
  );

  const totalPages = data?.total ? Math.ceil(data.total / pageLimit) : 0;
  const showTableLoading = isLoading || isFetching;

  // Check if any filters are applied
  const hasActiveFilters =
    searchValue ||
    (globalFilter?.clinics && globalFilter.clinics.length > 0) ||
    (globalFilter?.practitioners && globalFilter.practitioners.length > 0) ||
    (globalFilter?.deviceTypes && globalFilter.deviceTypes.length > 0) ||
    (globalFilter?.vendors && globalFilter.vendors.length > 0) ||
    (globalFilter?.patientStatuses && globalFilter.patientStatuses.length > 0) ||
    hasPreviousFilters ||
    hasCurrentFilters;

  const currentPatients = data?.patients ?? [];
  // Replace existing isAllSelected and isIndeterminate:
  const eligiblePatients = currentPatients.filter((p) => (p.notDownloadedReports?.length ?? 0) > 0);
  const isAllSelected =
    eligiblePatients.length > 0 && eligiblePatients.every((p) => selectedPatientIds.has(p.id));
  const isIndeterminate =
    !isAllSelected && eligiblePatients.some((p) => selectedPatientIds.has(p.id));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newIds = new Set(selectedPatientIds);
      const newReports = [...selectedReports];
      currentPatients.forEach((patient) => {
        // Skip patients with no previous reports
        if (!patient.previousReport) return;

        if (!newIds.has(patient.id)) {
          newIds.add(patient.id);
          newReports.push(patient.previousReport.id); // Add previous report ID
        }
      });
      setSelectedPatientIds(newIds);
      setSelectedReports(newReports);
    } else {
      const deselectedIds = new Set(currentPatients.map((p) => p.id));
      const deselectedReports = new Set(
        currentPatients.map((p) => p.previousReport?.id).filter(Boolean) // Collect previous report IDs
      );
      setSelectedPatientIds((prev) => {
        const next = new Set(prev);
        deselectedIds.forEach((id) => next.delete(id));
        return next;
      });
      setSelectedReports((prev) => prev.filter((r) => !deselectedReports.has(r)));
    }
  };

  const handleTogglePatient = (patient: PatientData) => {
    const isCurrentlySelected = selectedPatientIds.has(patient.id);
    if (isCurrentlySelected) {
      // Deselect: remove id and their previous report
      setSelectedPatientIds((prev) => {
        const next = new Set(prev);
        next.delete(patient.id);
        return next;
      });
      setSelectedReports((prev) => prev.filter((r) => r !== patient.previousReport?.id));
    } else {
      // Select: add id and their previous report
      setSelectedPatientIds((prev) => new Set(prev).add(patient.id));
      if (patient.previousReport?.id) {
        setSelectedReports((prev) =>
          [...prev, patient?.previousReport?.id].filter((id): id is string => id !== undefined)
        );
      }
    }
  };
  const downloadCount =
    selectedReports.length > 0 ? selectedReports.length : notDownloadedCount || 0;

  const isSelectAllDisabled = currentPatients.every(
    (p) => !p.previousReport || !p.previousReport.id 
  );

  useEffect(() => {
    setSelectedPatientIds(new Set());
    setSelectedReports([]);
  }, [previousReportFilters, currentReportFilters, globalFilter]);

  // Page handlers
  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  const handlePageLimitChange = (value: string | null) => {
    if (value) {
      const newLimit = parseInt(value, 10);
      dispatch(setPageLimit(newLimit));
      dispatch(setCurrentPage(1));
    }
  };

  const handleSearchChange = (value: string | undefined) => {
    if (!value || value.length >= 3) {
      dispatch(setSearchValue(value));
    } else {
      dispatch(setSearchValue(undefined));
    }
  };

  const handlePreviousReportFilterChange = (
    filters: string[],
    isRecentSigned?: boolean,
    isDownloaded?: boolean,
    recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE'
  ) => {
    if (hasCurrentFilters) {
      dispatch(clearCurrentReportFilters());
    }

    // Build the previous report filters object
    const newPreviousFilters: PreviousReportFilters = {};
    if (filters.length > 0) newPreviousFilters.colors = filters;
    if (isRecentSigned) {
      newPreviousFilters.isRecentSigned = isRecentSigned;
      // Only include recentSignedSortBy if isRecentSigned is true
      if (recentSignedSortBy) {
        newPreviousFilters.recentSignedSortBy = recentSignedSortBy;
      }
    }
    if (isDownloaded) newPreviousFilters.isDownloaded = isDownloaded;

    dispatch(setPreviousReportFilters(newPreviousFilters));
  };

  const handleCurrentReportFilterChange = (
    filters: string[],
    isNonPractitionerSelected: boolean,
    isPendingReviewSelected: boolean
  ) => {
    if (hasPreviousFilters) {
      dispatch(clearPreviousReportFilters());
    }

    // Build the current report filters object
    const newCurrentFilters: CurrentReportFilters = {};
    if (filters.length > 0) newCurrentFilters.colors = filters;
    if (isNonPractitionerSelected)
      newCurrentFilters.isNonPractitionerSigned = isNonPractitionerSelected;
    if (isPendingReviewSelected) newCurrentFilters.isPendingReview = isPendingReviewSelected;
    dispatch(setCurrentReportFilters(newCurrentFilters));
  };

  // Sort handler
  const handleSort = (field: string) => {
    let newDirection: SortDirection = 'ASC';

    if (sortBy === field) {
      // Cycling through: null -> ASC -> DESC -> null
      if (sortDirection === 'ASC') {
        newDirection = 'DESC';
      } else if (sortDirection === 'DESC') {
        newDirection = null;
        field = ''; // Clear sortBy when removing sort
      }
    }

    dispatch(
      setSort({
        sortBy: newDirection !== null ? field : '',
        sortDirection: newDirection
      })
    );
  };

  // Table rendering
  const renderTableBody = () => {
    if (error) {
      return (
        <Table.Tr>
          <Table.Td colSpan={100} p={0}>
            <Alert title="Error" color="red" px={20}>
              Error loading patients. Please refresh and try again
            </Alert>
          </Table.Td>
        </Table.Tr>
      );
    }

    if (!data?.patients || data.patients.length === 0) {
      return (
        <Table.Tr>
          <Table.Td colSpan={100} p={0}>
            <Center py={60}>
              <Box ta="center">
                <Text size="lg" fw={500} c="dimmed" mb={8}>
                  {hasActiveFilters || searchValue ? 'No patients found' : 'No patients available'}
                </Text>
                <Text size="sm" c="dimmed">
                  {hasActiveFilters || searchValue
                    ? 'Try adjusting your filters or search criteria'
                    : 'There are currently no active patients in the system'}
                </Text>
              </Box>
            </Center>
          </Table.Td>
        </Table.Tr>
      );
    }

    return currentPatients.map((patient: PatientData) => (
      <PatientTableRow
        key={patient.id}
        patient={patient}
        isSelected={selectedPatientIds.has(patient.id)}
        onToggle={handleTogglePatient}
      />
    ));
  };

  const renderTable = () => {
    if (showTableLoading) {
      return <TableSkeleton rows={pageLimit} />;
    }

    return (
      <ScrollArea h="70vh" type="always">
        <Table withTableBorder withColumnBorders>
          <TableHeader
            selectedPreviousReportFilters={previousReportFilters?.colors || []}
            selectedCurrentReportFilters={currentReportFilters?.colors || []}
            isNonPractitionerSignedCurrentReport={
              currentReportFilters?.isNonPractitionerSigned || false
            }
            isPendingReviewSelectedCurrentReport={currentReportFilters?.isPendingReview || false}
            isRecentSignedSelected={previousReportFilters?.isRecentSigned || false}
            isDownloadedSelected={previousReportFilters?.isDownloaded || false}
            onPreviousReportFiltersChange={handlePreviousReportFilterChange}
            onCurrentReportFiltersChange={handleCurrentReportFilterChange}
            isLoading={showTableLoading}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            onSelectAll={handleSelectAll}
            isSelectAllDisabled={isSelectAllDisabled}
          />
          <Table.Tbody className={styles.tableBody}>{renderTableBody()}</Table.Tbody>
        </Table>
      </ScrollArea>
    );
  };

  return (
    <Stack mx={20} gap={0}>
      <PageHeader
        patientCount={data?.total}
        isLoading={isLoading}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        notDownloadedCount={notDownloadedCount}
        isNotDownloadedLoading={isNotDownloadedLoading}
        onNotDownloadedClick={onDownloadAllNotDownloadedReports}
        currentReportFilters={currentReportFilters}
        previousReportFilters={previousReportFilters}
        onCurrentReportFilterChange={handleCurrentReportFilterChange}
        onPreviousReportFilterChange={handlePreviousReportFilterChange}
        isNewFiltersUXEnabled={isNewFiltersUXEnabled}
        selectedReportsCount={selectedReports.length > 0 ? selectedReports.length : undefined}
      />

      <div className={styles.tableContainer}>{renderTable()}</div>

      <div className={styles.footer}>
        {data && data?.total > 0 && (
          <Group justify="space-between" align="center" mt="md">
            {data?.total && (
              <Text size="sm" c="dimmed">
                Showing {(currentPage - 1) * pageLimit + 1}–
                {Math.min(currentPage * pageLimit, data.total)} of {data.total.toLocaleString()}
              </Text>
            )}

            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />

            <Group align="center" gap="sm">
              <Text size="sm" c="dimmed">
                Rows per page:
              </Text>
              <Select
                value={pageLimit.toString()}
                onChange={handlePageLimitChange}
                data={[
                  { value: '5', label: '5' },
                  { value: '10', label: '10' },
                  { value: '25', label: '25' },
                  { value: '50', label: '50' },
                  { value: '100', label: '100' }
                ]}
                size="sm"
                w={80}
                comboboxProps={{ withinPortal: false }}
              />
            </Group>
          </Group>
        )}
      </div>

      <AsyncBatchDownloadModal
        batchDownloadState={batchState}
        setBatchDownloadState={setBatchState}
        onDownload={handleBatchDownload}
        onClose={handleClose}
        notDownloadedCount={downloadCount}
        title={`${downloadCount} Reports`}
        downloadingMessage={`Preparing ${downloadCount} reports for download...`}
        successMessage={`${downloadCount} reports are ready for download!`}
      />
    </Stack>
  );
};

export default PatientListPage;
